import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, ShieldCheck, Mail, MessageSquare, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  [MessageSquare, 'Today', 'You pay $25 and we get your business its own texting number.'],
  [ShieldCheck, '3-5 business days (up to 2 weeks)', 'US phone carriers review and approve the number. This is required by law for every business that texts customers. Nobody can skip it.'],
  [Mail, 'While you wait', 'New appointment alerts are sent to you by email, so you miss nothing.'],
  [Clock, 'Approved', 'Texting turns on automatically and we email you. No action needed.'],
];

const FIELDS = [
  ['legal_business_name', 'Legal business name (as registered)', 'text', 'col-span-2'],
  ['ein', 'EIN (Tax ID, 9 digits)', 'text', 'col-span-2'],
  ['street', 'Street address', 'text', 'col-span-2'],
  ['city', 'City', 'text', ''],
  ['state', 'State (e.g. TX)', 'text', ''],
  ['postal_code', 'ZIP code', 'text', ''],
  ['website', 'Website (optional)', 'url', ''],
  ['contact_first_name', 'Contact first name', 'text', ''],
  ['contact_last_name', 'Contact last name', 'text', ''],
  ['contact_email', 'Contact email', 'email', ''],
  ['contact_phone', 'Contact phone', 'tel', ''],
];

export const SmsSetupDialog = ({ open, onClose, business, onCheckout }) => {
  const [form, setForm] = useState({
    legal_business_name: business?.business_name || '', business_type: 'PRIVATE_PROFIT', ein: '',
    street: business?.address || '', city: '', state: '', postal_code: '', website: business?.website || '',
    contact_first_name: '', contact_last_name: '', contact_email: business?.email || '', contact_phone: business?.owner_phone_number || '',
  });
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const soleProp = form.business_type === 'SOLE_PROPRIETOR';

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/business/${business.id}/sms-registration`, { ...form, ein: soleProp ? null : form.ein, website: form.website || null });
      await onCheckout();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? 'Please fill in every required field' : d || 'Could not save details');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white text-slate-900 border-slate-200 sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sms-setup-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Turn On Text Messaging ($25/month)</DialogTitle>
          <DialogDescription className="text-slate-500">
            Appointment confirmations, reminders and owner alerts sent from your own business texting number.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="sms-wait-explainer">
          <p className="font-semibold text-amber-900 mb-3">Heads up: texting isn't instant</p>
          <ol className="space-y-3">
            {STEPS.map(([Icon, title, desc]) => (
              <li key={title} className="flex gap-3">
                <Icon className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div><p className="text-sm font-medium text-slate-900">{title}</p><p className="text-sm text-slate-600">{desc}</p></div>
              </li>
            ))}
          </ol>
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-900 mb-1">Business details for carrier approval</p>
          <p className="text-xs text-slate-500 mb-3">Carriers check these against official records, so use your registered details.</p>
          <select value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} className="w-full h-10 px-3 mb-3 rounded-md border border-slate-300 bg-white text-slate-900 text-sm" data-testid="sms-business-type">
            <option value="PRIVATE_PROFIT">Company / LLC (has an EIN)</option>
            <option value="SOLE_PROPRIETOR">Sole proprietor (no EIN)</option>
            <option value="NON_PROFIT">Non-profit</option>
          </select>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.filter(([k]) => !(soleProp && k === 'ein')).map(([k, label, type, span]) => (
              <label key={k} className={`block ${span}`}>
                <span className="text-xs font-medium text-slate-600">{label}</span>
                <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-white border-slate-300 text-slate-900" data-testid={`sms-field-${k}`} />
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" data-testid="sms-understand-checkbox" />
          I understand texting starts after carrier approval (usually 3-5 business days, up to 2 weeks), and my details will be submitted for verification.
        </label>

        <Button onClick={submit} disabled={!agreed || saving} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="sms-continue-payment-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Continue to Payment ($25)
        </Button>
      </DialogContent>
    </Dialog>
  );
};
